<script setup>
// Botão de link do WhatsApp (`cta_url`): o texto + o botão como o cliente vê.
// No link do webchat (`webchat_kind: link`) o botão acende quando o cliente
// ABRE o link (`webchat_opened_at`, carimbado no Webchat::Session.exchange!).
// Ao tocar, o WhatsApp pergunta "Deseja abrir o link? Não / Sim": o "Não" fica
// no aparelho (a Meta não avisa), então sem carimbo o que se sabe é só
// "ainda não abriu" — nunca "recusou".
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes, webchatKind } = useMessageContext();
const { t } = useI18n();

// As chaves chegam camelizadas em parte dos caminhos: aceita as duas grafias.
const pick = (obj, snake) =>
  obj?.[snake] ?? obj?.[snake.replace(/_([a-z])/g, (_, c) => c.toUpperCase())];

const buttonText = computed(() =>
  pick(contentAttributes.value?.action?.parameters, 'display_text')
);

const isWebchatLink = computed(() => webchatKind.value === 'link');

const openedAt = computed(() => {
  const value = pick(contentAttributes.value, 'webchat_opened_at');
  return value ? Math.floor(new Date(value).getTime() / 1000) : null;
});

const statusText = computed(() => {
  if (!isWebchatLink.value) return '';
  if (openedAt.value) {
    return t('CONVERSATION.WEBCHAT.LINK_OPENED', {
      time: messageTimestamp(openedAt.value, 'HH:mm'),
    });
  }
  return t('CONVERSATION.WEBCHAT.LINK_NOT_OPENED');
});
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="cta-url">
    <div class="flex flex-col gap-3">
      <span v-if="content" v-dompurify-html="content" />
      <div
        v-if="buttonText"
        v-tooltip.top="
          isWebchatLink && !openedAt
            ? t('CONVERSATION.WEBCHAT.LINK_NOT_OPENED_TOOLTIP')
            : ''
        "
        class="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl"
        :class="
          openedAt
            ? 'bg-n-green-3 text-n-green-12 ring-1 ring-n-green-9'
            : 'bg-n-alpha-2 text-n-slate-12 border border-n-weak'
        "
      >
        <span
          class="size-4 shrink-0"
          :class="openedAt ? 'i-lucide-check' : 'i-lucide-external-link'"
        />
        {{ buttonText }}
      </div>
      <span
        v-if="statusText"
        class="text-xs"
        :class="openedAt ? 'text-n-green-11' : 'text-n-slate-11'"
      >
        {{ statusText }}
      </span>
    </div>
  </BaseBubble>
</template>
